import { createContext, useState, useRef, useEffect, useContext } from "react";

import { useModal } from "@newsela/angelou";
import { PropTypes } from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import useLazyContentSettingsGroupsGet from "@/admin/hooks/useLazyContentSettingsGroupsGet";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";

export const AdminContentSettingsContext = createContext();

// Load up all things that Content Settings needs available in Search:
// 1. Content Settings Mode Toggle State
// 2. Content Item selection counts
// 3. Apply Permissions modal
// 4. Disable Filters refs -- when in Content Settings mode, limit filter interactions

// it's not a lot, but if we ever decide to make it available on another surface area
// this context provider can be lifted and shifted
// Components that read from context may have to be updated accordingly.
const propTypes = {
  children: PropTypes.any,
};
const AdminContentSettingsProvider = ({ children }) => {
  const formMethods = useForm();

  const [
    applyPermissionsModalProps,
    setApplyPermissionsModalActive,
    triggerApplyPermissionsModalRef,
  ] = useModal(false);
  const [contentSettingsToggleEnabled, setContentSettingsToggleEnabled] =
    useState(false);
  const disableContentSettingsRef = useRef(false);

  const { data: user } = useUser();
  const { canModifyContentSettings } = getUserProperties(user);

  const [fetchContentSettingsGroups, { data: contentSettingsGroups }] =
    useLazyContentSettingsGroupsGet();

  const hasContentSettingGroups = !!contentSettingsGroups?.length;

  const enableContentSettingsFeatures =
    contentSettingsToggleEnabled && hasContentSettingGroups;

  useEffect(() => {
    if (canModifyContentSettings) {
      fetchContentSettingsGroups();
    }
  }, [canModifyContentSettings]);

  const sharedContext = {
    // TODO: style ? list out all the methods, properties, etc
    // being destructured for readability. Vague variables are vague,
    // makes it hard to debug and knowing exactly what is explicitly
    // within the shared context of Content Settings Mode in search
    contentSettingsToggleEnabled,
    setContentSettingsToggleEnabled,
    disableContentSettingsRef,
    applyPermissionsModalProps,
    setApplyPermissionsModalActive,
    triggerApplyPermissionsModalRef,
    contentSettingsGroups,
    hasContentSettingGroups,
    enableContentSettingsFeatures: enableContentSettingsFeatures || false,
  };

  return (
    <FormProvider {...formMethods}>
      <AdminContentSettingsContext.Provider value={sharedContext}>
        {children}
      </AdminContentSettingsContext.Provider>
    </FormProvider>
  );
};

AdminContentSettingsProvider.propTypes = propTypes;

export const useAdminContentSettingsContext = () => {
  return useContext(AdminContentSettingsContext);
};

export default AdminContentSettingsProvider;
